import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Upload, X, Image as ImageIcon, Loader2, Clock, CheckCircle, XCircle, FileText, Trash2 } from "lucide-react";

interface PortfolioPhoto {
  id: string;
  file_path: string;
  file_name: string;
  description?: string;
  status: 'pending' | 'approved' | 'rejected';
  rejection_reason?: string;
  rejection_viewed_at?: string;
  url: string;
}

interface InsuranceCertificate {
  id: string;
  file_path: string;
  file_name: string;
  expiry_date: string;
  status: 'pending' | 'approved' | 'rejected';
  rejection_reason?: string;
  rejection_viewed_at?: string;
  url: string;
}

interface TradesmanPortfolioUploadProps {
  tradesmanId: string;
  onUploadComplete: () => void;
  onValidationChange: (isValid: boolean) => void;
}

export const TradesmanPortfolioUpload = ({ tradesmanId, onUploadComplete, onValidationChange }: TradesmanPortfolioUploadProps) => {
  const [portfolioPhotos, setPortfolioPhotos] = useState<PortfolioPhoto[]>([]);
  const [insuranceCertificates, setInsuranceCertificates] = useState<InsuranceCertificate[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadType, setUploadType] = useState<'portfolio' | 'insurance'>('portfolio');
  const [photoDescription, setPhotoDescription] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [portfolioUploaded, setPortfolioUploaded] = useState(false);
  const [insuranceUploaded, setInsuranceUploaded] = useState(false);
  const portfolioInputRef = useRef<HTMLInputElement>(null);
  const insuranceInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchPortfolioData();
  }, [tradesmanId]);

  // Check validation requirements - REQUIRE insurance upload
  useEffect(() => {
    const hasInsurance = insuranceCertificates.some(cert => cert.status === 'approved' || cert.status === 'pending');
    const hasPortfolioPhotos = portfolioPhotos.length > 0;
    setInsuranceUploaded(hasInsurance);
    setPortfolioUploaded(hasPortfolioPhotos);
    // CHANGED: Require BOTH insurance AND portfolio photos for account completion
    const isValid = hasInsurance && hasPortfolioPhotos;
    onValidationChange(isValid);
  }, [portfolioPhotos, insuranceCertificates, onValidationChange]);

  const fetchPortfolioData = async () => {
    try {
      // Fetch portfolio photos
      const { data: portfolioData } = await supabase
        .from('portfolio_photos')
        .select('*')
        .eq('tradesman_id', tradesmanId)
        .order('created_at', { ascending: false });

      if (portfolioData) {
        const portfolioWithUrls = portfolioData.map(photo => ({
          ...photo,
          status: photo.status as 'pending' | 'approved' | 'rejected',
          url: supabase.storage.from('portfolio-photos').getPublicUrl(photo.file_path).data.publicUrl,
        }));
        setPortfolioPhotos(portfolioWithUrls);
      }

      // Fetch insurance certificates
      const { data: insuranceData } = await supabase
        .from('insurance_certificates')
        .select('*')
        .eq('tradesman_id', tradesmanId)
        .order('created_at', { ascending: false });

      if (insuranceData) {
        const insuranceWithUrls = await Promise.all(insuranceData.map(async (cert) => {
          const { data: signed } = await supabase.storage
            .from('insurance-certificates')
            .createSignedUrl(cert.file_path, 60 * 60);
          return {
            ...cert,
            status: cert.status as 'pending' | 'approved' | 'rejected',
            url: signed?.signedUrl || '',
          };
        }));
        setInsuranceCertificates(insuranceWithUrls);
      }
    } catch (error: any) {
      const msg = error?.message?.toString() || '';
      // Gracefully ignore permission errors (RLS) and allow uploads to proceed
      if (msg.toLowerCase().includes('permission denied')) {
        console.warn('Portfolio data fetch blocked by RLS policies; continuing.');
        return;
      }
      toast({
        title: "Error loading portfolio data",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  // Resolve current tradesman profile id if not provided
  const resolveTradesmanId = async (): Promise<string | null> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const { data } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
      return data?.id ?? null;
    } catch {
      return null;
    }
  };

  // Robust storage upload with retry and explicit content type
  const uploadWithRetry = async (
    bucket: string,
    path: string,
    file: File,
    retries = 1
  ) => {
    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(path, file, { contentType: file.type || undefined, cacheControl: '3600', upsert: false });
      if (error) throw error;
      return data;
    } catch (err: any) {
      if (retries > 0 && (err?.message?.includes('Failed to fetch') || err?.name === 'TypeError')) {
        await new Promise(r => setTimeout(r, 800));
        return uploadWithRetry(bucket, path, file, retries - 1);
      }
      throw err;
    }
  };
  const uploadPortfolioPhoto = async (file: File) => {
    if (portfolioPhotos.length >= 20) {
      toast({
        title: "Maximum photos reached",
        description: "You can only upload up to 20 portfolio photos.",
        variant: "destructive",
      });
      return null;
    }

    try {
      const tId = tradesmanId || await resolveTradesmanId();
      if (!tId) {
        toast({
          title: "Profile not ready",
          description: "Please wait a moment and try again.",
          variant: "destructive",
        });
        return null;
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${tId}/${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;

      // Upload to storage
      const storageData = await uploadWithRetry('portfolio-photos', fileName, file);
      
      if (!storageData) {
        throw new Error('Failed to upload file to storage');
      }

      // Save to database using minimal returning to avoid SELECT permission issues
      const { error: insertError } = await (supabase.from('portfolio_photos') as any)
        .insert({
          tradesman_id: tId,
          file_path: storageData.path,
          file_name: file.name,
          file_size: file.size,
          description: photoDescription.trim() || null,
        }, { returning: 'minimal' });
      if (insertError) { await supabase.storage.from('portfolio-photos').remove([storageData.path]).catch(() => {}); throw insertError; }

      // Create an optimistic record; server will reflect it later
      const dbData = {
        id: `temp-${Date.now()}`,
        file_path: storageData.path,
        file_name: file.name,
        description: photoDescription.trim() || '',
        status: 'pending',
        rejection_reason: null,
        rejection_viewed_at: null,
      } as any;


      // Optimistically add to UI so users see the photo immediately
      const newPhoto: PortfolioPhoto = {
        id: dbData.id,
        file_path: dbData.file_path,
        file_name: dbData.file_name,
        description: dbData.description || '',
        status: (dbData.status as 'pending' | 'approved' | 'rejected') || 'pending',
        rejection_reason: dbData.rejection_reason || undefined,
        rejection_viewed_at: dbData.rejection_viewed_at || undefined,
        url: supabase.storage.from('portfolio-photos').getPublicUrl(dbData.file_path).data.publicUrl,
      };
      setPortfolioPhotos(prev => [newPhoto, ...prev]);

      setPhotoDescription("");
      return dbData;
    } catch (error) {
      console.error('Error uploading portfolio photo:', error);
      throw error;
    }
  };

  const uploadInsuranceCertificate = async (file: File) => {
    if (!expiryDate) {
      toast({
        title: "Expiry date required",
        description: "Please select the expiry date for your insurance certificate.",
        variant: "destructive",
      });
      return null;
    }

    try {
      const tId = tradesmanId || await resolveTradesmanId();
      if (!tId) {
        toast({
          title: "Profile not ready",
          description: "Please wait a moment and try again.",
          variant: "destructive",
        });
        return null;
      }
      const fileExt = file.name.split('.').pop();
      const fileName = `${tId}/${Date.now()}-insurance.${fileExt}`;

      // Upload to storage
      const storageData = await uploadWithRetry('insurance-certificates', fileName, file);
      
      if (!storageData) {
        throw new Error('Failed to upload file to storage');
      }

      // Save to database using minimal returning to avoid SELECT permission issues
      const { error: insertError } = await (supabase.from('insurance_certificates') as any)
        .insert({
          tradesman_id: tId,
          file_path: storageData.path,
          file_name: file.name,
          file_size: file.size,
          expiry_date: expiryDate,
        }, { returning: 'minimal' });
      if (insertError) { await supabase.storage.from('insurance-certificates').remove([storageData.path]).catch(() => {}); throw insertError; }


      const dbData: any = {
        id: `temp-${Date.now()}`,
        file_path: storageData.path,
        file_name: file.name,
        expiry_date: expiryDate,
        status: 'pending',
      };



      // Optimistically add to UI so users see the certificate immediately
      const { data: signed } = await supabase.storage
        .from('insurance-certificates')
        .createSignedUrl(dbData.file_path, 60 * 60);
      const newCert: InsuranceCertificate = {
        id: dbData.id,
        file_path: dbData.file_path,
        file_name: dbData.file_name,
        expiry_date: dbData.expiry_date,
        status: (dbData.status as 'pending' | 'approved' | 'rejected') || 'pending',
        rejection_reason: dbData.rejection_reason || undefined,
        rejection_viewed_at: dbData.rejection_viewed_at || undefined,
        url: signed?.signedUrl || '',
      };
      setInsuranceCertificates(prev => [newCert, ...prev]);

      // Send notification to admins (non-blocking)
      try {
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('full_name, email, company_name')
          .eq('id', tradesmanId)
          .single();

        if (profileData && !profileError) {
          const notificationPayload = {
            insurance_certificate_id: dbData.id,
          };
          // Send notification but don't fail upload if notification fails
          supabase.functions.invoke('notify-admin-insurance-approval', {
            body: notificationPayload
          }).catch(err => console.warn('Admin notification failed:', err));

        }
      } catch (error) {
        console.warn('Failed to send admin notification:', error);
      }

      setExpiryDate("");
      return dbData;
    } catch (error) {
      console.error('Error uploading insurance certificate:', error);
      throw error;
    }
  };

  const deleteInsuranceCertificate = async (certId: string, filePath: string) => {
    const confirmed = window.confirm('Are you sure you want to delete this insurance certificate? This action cannot be undone.');
    if (!confirmed) return;

    try {
      console.log('=== STARTING DELETE OPERATION ===');
      console.log('Certificate ID:', certId);
      console.log('File path:', filePath);
      console.log('Current user:', await supabase.auth.getUser());
      
      // Immediately remove from UI to provide instant feedback
      setInsuranceCertificates(prev => prev.filter(cert => cert.id !== certId));
      
      console.log('=== ATTEMPTING DATABASE DELETE ===');
      // Delete from database first
      const { data, error: dbError } = await supabase
        .from('insurance_certificates')
        .delete()
        .eq('id', certId);

      console.log('Database delete response:', { data, error: dbError });

      if (dbError) {
        console.error('Database deletion error:', dbError);
        // Restore the certificate to the UI if database deletion failed
        toast({
          title: "Delete failed",
          description: `Database error: ${dbError.message}`,
          variant: "destructive",
        });
        fetchPortfolioData();
        return;
      }

      console.log('=== DATABASE DELETE SUCCESSFUL ===');

      // Delete from storage (non-critical, file may not exist)
      console.log('=== ATTEMPTING STORAGE DELETE ===');
      const { error: storageError } = await supabase.storage
        .from('insurance-certificates')
        .remove([filePath]);

      if (storageError) {
        console.warn('Storage deletion error (file may not exist):', storageError);
        // Don't throw here as the database record is already deleted
      }

      toast({
        title: "Certificate deleted",
        description: "Your insurance certificate has been removed successfully.",
      });

      console.log('=== DELETE OPERATION COMPLETED ===');
    } catch (error: any) {
      console.error('=== UNEXPECTED ERROR ===', error);
      toast({
        title: "Error deleting certificate",
        description: error.message,
        variant: "destructive",
      });
      // Refresh to restore accurate state
      fetchPortfolioData();
    }
  };

  const handleFileUpload = async (files: FileList) => {
    if (!files.length) return;

    setIsUploading(true);

    try {
      const file = files[0];

      // Validate file type
      if (uploadType === 'portfolio' && !file.type.startsWith('image/')) {
        throw new Error('Portfolio uploads must be image files');
      }

      if (uploadType === 'insurance' && !file.type.includes('pdf') && !file.type.startsWith('image/')) {
        throw new Error('Insurance certificates must be PDF or image files');
      }

      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('File is too large. Maximum size is 10MB.');
      }

      let uploadResult = null;

      if (uploadType === 'portfolio') {
        uploadResult = await uploadPortfolioPhoto(file);
        if (uploadResult) {
          toast({
            title: "Portfolio photo uploaded",
            description: "Your photo is now pending approval from our team.",
          });
        }
      } else {
        uploadResult = await uploadInsuranceCertificate(file);
        if (uploadResult) {
          toast({
            title: "Insurance certificate uploaded",
            description: "Your certificate is now pending approval from our team.",
          });
        }
      }

      // Only call these if upload was successful
      if (uploadResult) {
        if (uploadType === 'portfolio') setPortfolioUploaded(true);
        if (uploadType === 'insurance') setInsuranceUploaded(true);
        await fetchPortfolioData();
        onUploadComplete();
      }
    } catch (error: any) {
      console.error('File upload error:', error);
      toast({
        title: "Upload failed",
        description: error.message || "An unexpected error occurred during upload.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      // Reset file inputs to allow re-uploading the same file
      if (portfolioInputRef.current) portfolioInputRef.current.value = '';
      if (insuranceInputRef.current) insuranceInputRef.current.value = '';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  return (
    <div className="w-full max-w-full overflow-x-hidden break-words space-y-3">
      {/* Portfolio Photos Section */}
      <Card className="w-full">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <ImageIcon className="h-4 w-4" />
            Portfolio Photos ({portfolioPhotos.length}/20)
          </CardTitle>
          <CardDescription className="text-sm">
            Upload photos of your past work. All photos require approval before going live.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 pt-0">
          {portfolioPhotos.length < 20 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="photoDescription">Photo Description (Optional)</Label>
                <Textarea
                  id="photoDescription"
                  value={photoDescription}
                  onChange={(e) => setPhotoDescription(e.target.value)}
                  placeholder="Describe the work shown in this photo (optional)..."
                  rows={2}
                />
              </div>
              <Button
                onClick={() => {
                  setUploadType('portfolio');
                  portfolioInputRef.current?.click();
                }}
                disabled={isUploading}
                variant={portfolioUploaded ? 'success' : 'outline'}
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload Portfolio Photo
              </Button>
            </div>
          )}

          {/* Uploaded Photos - Shown immediately below upload button */}
          {portfolioPhotos.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Uploaded Photos</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {portfolioPhotos.map((photo) => (
                  <Card key={photo.id} className="overflow-hidden">
                    <div className="aspect-video relative">
                      <img
                        src={photo.url}
                        alt={photo.description}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-2 right-2">
                        <Badge className={getStatusColor(photo.status)} variant="secondary">
                          {getStatusIcon(photo.status)}
                          <span className="ml-1 capitalize text-xs">{photo.status}</span>
                        </Badge>
                      </div>
                    </div>
                    <CardContent className="p-2">
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {photo.description || 'No description provided'}
                      </p>
                      {photo.status === 'rejected' && photo.rejection_reason && (
                        <p className="text-xs text-red-600 mt-1">
                          Reason: {photo.rejection_reason}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Insurance Certificate Section */}
      <Card className="w-full">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-4 w-4" />
            Public Liability Insurance
          </CardTitle>
          <CardDescription className="text-sm">
            Upload your current public liability insurance certificate. Required to bid on jobs.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 pt-0">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="expiryDate">Certificate Expiry Date</Label>
              <Input
                id="expiryDate"
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <Button
              onClick={() => {
                setUploadType('insurance');
                insuranceInputRef.current?.click();
              }}
              disabled={isUploading}
              variant={insuranceUploaded ? 'success' : 'hero'}
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Insurance Certificate
            </Button>
          </div>

          {/* Uploaded Certificates - Shown immediately below upload button */}
          {insuranceCertificates.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Uploaded Certificates</h4>
              <div className="space-y-2">
                {insuranceCertificates.map((cert) => (
                  <Card key={cert.id} className="p-3 w-full">
                    <div className="flex items-start justify-between gap-2 w-full">
                      <div className="flex-1 min-w-0 space-y-1 pr-2">
                        <p 
                          className="font-medium text-sm truncate max-w-[180px]" 
                          title={cert.file_name}
                        >
                          {cert.file_name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Expires: {new Date(cert.expiry_date).toLocaleDateString()}
                        </p>
                        {cert.status === 'rejected' && cert.rejection_reason && (
                          <p className="text-xs text-red-600 break-words mt-1">
                            Reason: {cert.rejection_reason}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <Badge className={`${getStatusColor(cert.status)} text-xs px-1.5 py-0.5`} variant="secondary">
                          {getStatusIcon(cert.status)}
                          <span className="ml-1 capitalize text-xs">{cert.status}</span>
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteInsuranceCertificate(cert.id, cert.file_path)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/20 p-1 h-7 w-7"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Hidden file inputs */}
      <input
        ref={portfolioInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
      />
      
      <input
        ref={insuranceInputRef}
        type="file"
        accept=".pdf,image/*"
        className="hidden"
        onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
      />

      {isUploading && (
        <Card className="p-3 sm:p-4 w-full">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
            <p className="text-sm">Uploading file...</p>
          </div>
        </Card>
      )}
    </div>
  );
};